import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { preloadRecaptcha } from '@/lib/recaptcha';
import { CookieNotice } from '@/components/layout/CookieNotice';
import { MaintenanceBanner } from '@/components/layout/MaintenanceBanner';
import { ThemeToggle } from '@/components/shared/ThemeToggle';
import { useSessionStore } from '@/store/session-store';
import { LoginForm } from './components/LoginForm';
import { LoginIntro } from './components/LoginIntro';
import { RecaptchaNotice } from './components/RecaptchaNotice';
import { SessionNotice } from './components/SessionNotice';
import { SupportLinks } from './components/SupportLinks';
import { readSessionNotice } from './utils/session-notice';

/**
 * Unauthenticated entry point of the portal (US-001). Rendered outside the application shell.
 * `maintenanceActive` arrives from the layout composition (LoginScreen) because this feature may not
 * import the maintenance feature itself; while true, login is disabled (Proweb-38 follow-up).
 */
export function LoginPage({
  maintenanceActive = false,
}: {
  readonly maintenanceActive?: boolean;
}): React.JSX.Element {
  const providerId = useSessionStore((state) => state.providerId);
  const sessionNotice = readSessionNotice(useLocation().state);

  // Started here, not at submit: v3 needs to observe the interaction to score it.
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  // Reaching the login screen with a live session means the provider navigated backwards into it; send
  // them on rather than asking them to sign in twice.
  if (providerId !== null) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-canvas via-canvas-mid to-canvas-end">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:bg-surface focus:p-3"
      >
        Skip to main content
      </a>

      <MaintenanceBanner />

      <header className="flex items-center justify-end gap-5 px-6 py-5">
        <SupportLinks />
        <ThemeToggle />
      </header>

      <main id="main" className="flex flex-1 items-start justify-center px-5 pb-16">
        <div className="border-line w-full max-w-[440px] rounded-md border bg-surface px-9 pt-8 pb-6 shadow-[0_2px_4px_rgba(20,53,94,.05),0_24px_60px_-34px_rgba(20,53,94,.45)]">
          <LoginIntro />

          <div className="mt-6 border-t border-line pt-5">
            {sessionNotice !== null && <SessionNotice message={sessionNotice} />}

            <LoginForm maintenanceActive={maintenanceActive} />
            <RecaptchaNotice />
          </div>

          <p className="text-faint mt-5 border-t border-line pt-4 text-center text-xs">
            © 2026 Bankers Fidelity. All rights reserved.
          </p>
        </div>
      </main>

      <CookieNotice />
    </div>
  );
}
